import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { jobsApi } from '@/lib/api';
import useAppStore from '@/stores/appStore';

export const useMyJobs = () => {
  const currentUser = useAppStore((state) => state.currentUser);
  const viewAsUser = useAppStore((state) => state.viewAsUser);
  const effectiveUserId = viewAsUser?.id || currentUser?.id;

  return useQuery({
    queryKey: ['myJobs', effectiveUserId],
    queryFn: async () => {
      const { data } = await jobsApi.getMy();
      return data;
    },
    refetchInterval: 30000, // Refetch every 30 seconds for technicians
  });
};

export const useJob = (id) => {
  return useQuery({
    queryKey: ['job', id],
    queryFn: async () => {
      const { data } = await jobsApi.get(id);
      return data;
    },
    enabled: !!id,
  });
};

export const useMarkNavigating = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (id) => jobsApi.markNavigating(id),
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: ['job', id] });
      queryClient.invalidateQueries({ queryKey: ['myJobs'] });
    },
  });
};

export const useMarkArrived = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ id, gps }) => jobsApi.markArrived(id, gps),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: ['job', id] });
      queryClient.invalidateQueries({ queryKey: ['myJobs'] });
    },
  });
};

export const useVerifyStartOtp = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ id, otp }) => jobsApi.verifyStartOtp(id, otp),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: ['job', id] });
      queryClient.invalidateQueries({ queryKey: ['myJobs'] });
    },
  });
};

export const useSubmitChecklist = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ id, items }) => jobsApi.submitChecklist(id, items),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: ['job', id] });
    },
  });
};

export const useRecordPayment = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ id, payment }) => jobsApi.recordPayment(id, payment),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: ['job', id] });
    },
  });
};

export const useDeletePayment = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (id) => jobsApi.deletePayment(id),
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: ['job', id] });
    },
  });
};

export const useMarkComplete = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ id, data }) => jobsApi.markComplete(id, data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: ['job', id] });
      queryClient.invalidateQueries({ queryKey: ['myJobs'] });
    },
  });
};

export const useVerifyCloseOtp = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ id, otp }) => jobsApi.verifyCloseOtp(id, otp),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: ['job', id] });
      queryClient.invalidateQueries({ queryKey: ['myJobs'] });
    },
  });
};